import {useMemo, useState} from 'react'
import {OverlayTrigger, Popover, Tooltip} from 'react-bootstrap'
import {
  buildTaskDateTime,
  formatTaskDateTimeDisplay,
  parseTaskDateTime,
} from '../../../../../../../_metronic/helpers/TaskDateTime'
import {isTaskOverdue} from '../../../../../../../_metronic/helpers/AssetHelpers'

type Props = {
  expectedDate?: string | null
  endDate?: string | null
  onSaveDate: (date: string | null) => Promise<void> | void
  iconSizeClass?: string
  align?: 'bottom-start' | 'bottom' | 'bottom-end'
}

const isValidDateValue = (value?: string | null) => {
  if (!value) return false
  const normalized = value.trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') return false
  return Boolean(parseTaskDateTime(normalized).date)
}

const toDate = (value?: string | null) => {
  const parsed = parseTaskDateTime(value)
  if (!parsed.date) return null
  const date = new Date(`${parsed.date}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

const ExpectedDateQuickPicker = ({
  expectedDate,
  endDate,
  onSaveDate,
  iconSizeClass = 'fs-4',
  align = 'bottom-end',
}: Props) => {
  const [showPicker, setShowPicker] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const normalizedExpected = isValidDateValue(expectedDate) ? expectedDate!.trim() : ''
  const expectedDateObj = toDate(normalizedExpected)
  const endDateObj = toDate(endDate)
  const dateInputValue = parseTaskDateTime(normalizedExpected).date ?? ''

  const isDeliveredLate = useMemo(() => {
    if (!expectedDateObj || !endDateObj) return false
    return expectedDateObj < endDateObj
  }, [expectedDateObj, endDateObj])

  const isOverdue = useMemo(() => {
    if (!normalizedExpected || endDateObj) return false
    return isTaskOverdue(normalizedExpected, {delivered: false})
  }, [endDateObj, normalizedExpected])

  const tooltipText = useMemo(() => {
    if (!expectedDateObj) return 'Esta tarefa não possui prazo definido.'
    if (endDateObj) {
      return isDeliveredLate ? 'Tarefa entregue com atraso. ⏰' : 'Tarefa entregue no prazo!'
    }
    return isOverdue
      ? 'Prazo expirado: a tarefa está atrasada.'
      : `Prazo máximo definido para esta tarefa: ${formatTaskDateTimeDisplay(normalizedExpected)}`
  }, [endDateObj, expectedDateObj, isDeliveredLate, isOverdue, normalizedExpected])

  const iconClassName = useMemo(() => {
    if (!expectedDateObj) return `bi bi-calendar-event ${iconSizeClass}`
    if (isDeliveredLate || isOverdue) return `bi bi-calendar-x ${iconSizeClass} text-danger`
    return `bi bi-calendar-event ${iconSizeClass} text-primary`
  }, [expectedDateObj, iconSizeClass, isDeliveredLate, isOverdue])

  const popover = (
    <Popover id='expected-date-quick-picker-popover'>
      <Popover.Header as='h3'>Prazo da tarefa</Popover.Header>
      <Popover.Body>
        <div className='d-flex flex-column gap-2' onClick={(e) => e.stopPropagation()}>
          <input
            type='date'
            className='form-control form-control-sm'
            value={dateInputValue}
            disabled={isSaving}
            onChange={async (event) => {
              const value = event.target.value?.trim()
              const nextDate = value ? buildTaskDateTime(value, null, 'expected') : null
              setIsSaving(true)
              try {
                await onSaveDate(nextDate)
                setShowPicker(false)
              } finally {
                setIsSaving(false)
              }
            }}
          />
          <button
            type='button'
            className='btn btn-sm btn-light-danger'
            disabled={isSaving || !dateInputValue}
            onClick={async () => {
              setIsSaving(true)
              try {
                await onSaveDate(null)
                setShowPicker(false)
              } finally {
                setIsSaving(false)
              }
            }}
          >
            Limpar data
          </button>
        </div>
      </Popover.Body>
    </Popover>
  )

  return (
    <OverlayTrigger placement='top' delay={{show: 50, hide: 100}} overlay={<Tooltip>{tooltipText}</Tooltip>}>
      <span onClick={(e) => e.stopPropagation()}>
        <OverlayTrigger
          trigger='click'
          placement={align}
          rootClose
          show={showPicker}
          onToggle={(nextShow) => {
            if (!isSaving) {
              setShowPicker(nextShow)
            }
          }}
          overlay={popover}
        >
          <button
            type='button'
            className='btn btn-sm btn-icon p-0 border-0 bg-transparent'
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            disabled={isSaving}
          >
            <i className={iconClassName}></i>
          </button>
        </OverlayTrigger>
      </span>
    </OverlayTrigger>
  )
}

export {ExpectedDateQuickPicker}
